'use client';
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { socialApi } from './client';
import { createClientResourceCache } from '@/lib/client-resource-cache';

export function useCachedSocialResource<T>(url: string | null, identityKey: string, { keepPreviousData = false, staleTime = 60_000, accessKey }: { keepPreviousData?: boolean; staleTime?: number; accessKey?: (data: T) => string } = {}) {
  const cache = useMemo(() => createClientResourceCache<T>(identityKey, staleTime, accessKey, socialApi), [identityKey, staleTime, accessKey]);
  const snapshot = useSyncExternalStore(cache.subscribe, cache.getSnapshot, cache.getServerSnapshot);
  useEffect(() => () => cache.dispose(), [cache]);
  useEffect(() => {
    if (!url) return;
    void cache.load(url);
    const revalidate = () => { if (!document.hidden) void cache.load(url); };
    const accessChanged = () => { cache.invalidate(); void cache.load(url); };
    window.addEventListener('focus', revalidate); document.addEventListener('visibilitychange', revalidate);
    window.addEventListener('social:access-changed', accessChanged);
    return () => { window.removeEventListener('focus', revalidate); document.removeEventListener('visibilitychange', revalidate); window.removeEventListener('social:access-changed', accessChanged); };
  }, [cache, url]);
  const entry = url ? snapshot.entries.get(url) : undefined, hasCurrentData = entry?.data !== undefined;
  return {
    data: entry?.data ?? (keepPreviousData ? snapshot.lastData : undefined),
    hasCurrentData, error: entry?.error,
    loading: url !== null && !hasCurrentData && !entry?.error,
    refreshing: Boolean(hasCurrentData && entry?.loading),
    reload: () => { if (url) cache.reload(url); },
    update: (data: T, options?: { invalidateOthers?: boolean }) => { if (url) cache.update(url, data, entry?.data, options); },
    remember: (alias: string) => { if (url) cache.remember(url, alias); },
  };
}
